import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store";
import { WithAppStore } from "@/store/types";
import { Dexie, liveQuery, Subscription } from "dexie";
import { SourceObservable } from "@/store/sources/SourceObservable";
import { SourceKind } from "@/store/sources/types";

type NoteSourceItemTuple = [note_id: Uuid, source_id: Uuid, model_id: Uuid];

export class NoteSourceListObservable {
  private store: AppStore;
  private noteId: Uuid;

  isLoading: boolean;
  liveQuerySubscription?: Subscription;
  subscribedNoteSourceItems: NoteSourceItemTuple[] = [];

  constructor({ store, noteId }: { noteId: Uuid } & WithAppStore) {
    this.store = store;
    this.noteId = noteId;
    this.isLoading = true;

    makeObservable<this, "store" | "noteId">(this, {
      store: false,
      noteId: observable,

      isLoading: observable,
      liveQuerySubscription: observable,
      subscribedNoteSourceItems: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,

      supportedSourceKinds: computed,
      allSourceIds: computed,
      allSources: computed,
      allEmailSources: computed,
      allTextMessageSources: computed,
      allSupportedSources: computed,
    });

    onBecomeObserved(this, "subscribedNoteSourceItems", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "subscribedNoteSourceItems", () => this.unsubscribeLiveQuery());
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.store.noteSources.localTable
        .where("[note_id+source_id+model_id]")
        .between(
          [this.noteId, Dexie.minKey, Dexie.minKey],
          [this.noteId, Dexie.maxKey, Dexie.maxKey]
        )
        .keys();
    }).subscribe({
      next: rows => {
        runInAction(() => {
          this.subscribedNoteSourceItems = rows as unknown as NoteSourceItemTuple[];
          this.isLoading = false;
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get supportedSourceKinds(): SourceKind[] {
    const supportedKinds: SourceKind[] = [
      "EMAIL",
      "API_REQUEST",
      "API_REQUEST_VIA_ZAPIER",
      "AUDIO",
      "WEB_CLIP",
    ];

    if (this.store.spaceAccountFeatureFlags.getFlagStatus("text-mem")) {
      supportedKinds.push("TEXT_MESSAGE");
    }

    return supportedKinds;
  }

  get allSourceIds(): Set<Uuid> {
    return new Set(
      this.subscribedNoteSourceItems.map(([_note_id, source_id, _note_source_item_id]) => source_id)
    );
  }

  get allSources(): SourceObservable[] {
    return Array.from(this.allSourceIds)
      .map(id => this.store.sources.get(id))
      .filter(source => !!source);
  }

  get allEmailSources(): SourceObservable[] {
    return this.allSources.filter(source => source.sourceKind === "EMAIL");
  }

  get allTextMessageSources(): SourceObservable[] {
    return this.allSources.filter(source => source.sourceKind === "TEXT_MESSAGE");
  }

  get allSupportedSources(): SourceObservable[] {
    const filteredSources = this.allSources.filter(source =>
      this.supportedSourceKinds.includes(source.sourceKind)
    );

    // Group sources by kind
    const emailSources = filteredSources.filter(source => source.sourceKind === "EMAIL");
    const textMessageSources = filteredSources.filter(
      source => source.sourceKind === "TEXT_MESSAGE"
    );
    const apiRequestSources = filteredSources.filter(source => source.sourceKind === "API_REQUEST");
    const apiRequestViaZapierSources = filteredSources.filter(
      source => source.sourceKind === "API_REQUEST_VIA_ZAPIER"
    );
    const webClipSources = filteredSources.filter(source => source.sourceKind === "WEB_CLIP");
    const audioSources = filteredSources.filter(source => source.sourceKind === "AUDIO");

    // Take all email sources but only first text message source
    return [
      ...emailSources,
      ...(textMessageSources.length > 0 ? [textMessageSources[textMessageSources.length - 1]] : []),
      ...apiRequestSources,
      ...apiRequestViaZapierSources,
      ...webClipSources,
      ...audioSources,
    ];
  }
}
